import styles from './playerPropAnalyzer.module.css';
import React, { useMemo, useState, useEffect } from 'react';
import { getPlayers, getPlayerStats, getPlayerPropInsights } from '../../service/statsService';
import { getColumnName } from '../../utils/columnUtils';
import { isFeatureSupported } from '../../utils/featureUtil';

// PlayerPropAnalyzer renders a Bloomberg-themed analyzer for player props
// using CSS modules and simple mock data. It adapts shown props per sport.
// - For NBA: shows Points and Assists
// - For MLB: shows Home Runs (HR)
// Other sports fall back to a generic view and reuse points where possible.
const PlayerPropAnalyzer = ({ sportType, betCardData }) => {

    

    
    // Mock selectable players by sport

    // Opponent list derived from betCardData
    
    const [selectablePlayers, setSelectablePlayers] = useState({});
    // Component state for selectors
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const selectableTeams = () => {
        const home = betCardData?.homeTeam ? { 'teamName': betCardData.homeTeam, 'teamId': betCardData.homeTeamStatspyderId } : 'Home';
        const away = betCardData?.awayTeam ? { 'teamName': betCardData.awayTeam, 'teamId': betCardData.awayTeamStatspyderId } : 'Away';
        return [home, away];
    };

    const [selectedTeam, setSelectedTeam] = useState(null);
    const [selectedFilter, setSelectedFilter] = useState('Last 10 Games');
    const [selectedPlayerData, setSelectedPlayerData] = useState(null);
    const [playerDataColumns, setPlayerDataColumns] = useState([]);
    const [playerPropInsights, setPlayerPropInsights] = useState(null);
    const [hitRateThresholds, setHitRateThresholds] = useState({});
    const [selectedStat, setSelectedStat] = useState(null);
    
    // Section visibility states
    const [showQuickInsights, setShowQuickInsights] = useState(true);
    const [showHitRateCalculator, setShowHitRateCalculator] = useState(true);
    const [showGameLog, setShowGameLog] = useState(true);


    useEffect(() => {
        if (selectedPlayer) {
            const request = {
                'playerId': selectedPlayer,
                'sport': sportType,
                'teamId': selectedTeam.teamId
            }
            getPlayerStats(request).then(response => {
                console.log('Player stats:', response);
                if (response.data && response.data.success) {
                    const results = response.data.results;
                    if (results && results.length > 0) {
                        let newColumns = Object.keys(results[0]);
                        setPlayerDataColumns(newColumns);
                        setSelectedPlayerData(results);
                    }
                }
            }).catch(error => {
                console.error('Error fetching player stats:', error);
            });

            if(isFeatureSupported('playerPropInsights', sportType)) {
                const playerPropInsightsRequest = {
                    'playerId': selectedPlayer,
                    'sport': sportType,
                    'teamId': selectedTeam.teamId,
                    'oppId': selectableTeams().find(team => team.teamId !== selectedTeam.teamId).teamId
                }
                getPlayerPropInsights(playerPropInsightsRequest).then(response => {
                    console.log('Player prop insights:', response);
                    if (response.data && response.data.success && response.data.results) {
                        setPlayerPropInsights(response.data.results);
                    }
                }).catch(error => {
                    console.error('Error fetching player prop insights:', error);
                });
            }
        }
        else {
            setTimeout(() => {
                setSelectedPlayerData(null);
                setPlayerDataColumns([]);
                setPlayerPropInsights(null);
            }, 100);
        }
    }, [selectedPlayer]);

    useEffect(() => {
        fetchPlayers();
    }, [sportType]);

    // Set initial selected stat when player data changes
    useEffect(() => {
        const numericColumns = getNumericColumns();
        if (numericColumns.length > 0 && !selectedStat) {
            setSelectedStat(numericColumns[0]);
        }
    }, [selectedPlayerData]);
 
    // useEffect(() => {
    //     if(selectedTeam) {
    //         setSelectedPlayer(null);
    //     }
    // }, [selectedPlayersData]);

    // Helper to color-code OVER/UNDER results
    const getResultColor = (result) => {
        if (result === 'OVER') return 'var(--bloomberg-green)';
        if (result === 'UNDER') return 'var(--bloomberg-red)';
        return 'var(--bloomberg-orange)';
    };

    // Calculate hit rate for a specific column and threshold
    const calculateHitRate = (columnName, threshold, data = selectedPlayerData) => {
        if (!data || !Array.isArray(data) || data.length === 0) {
            return { hits: 0, total: 0, percentage: 0, impliedProbability: 0 };
        }

        const hits = data.filter(game => {
            const value = parseFloat(game[columnName]);
            return !isNaN(value) && value >= threshold;
        }).length;

        const total = data.length;
        const percentage = total > 0 ? (hits / total) * 100 : 0;
        const impliedProbability = percentage > 0 ? (100 / percentage) : 0;

        return {
            hits,
            total,
            percentage: percentage * 10 / 10, // Round to 1 decimal
            impliedProbability: impliedProbability
            // impliedProbability: Math.round(impliedProbability * 10) / 10
        };
    };

    // Update threshold for a specific column
    const updateThreshold = (columnName, value) => {
        // Allow empty string to clear the threshold
        if (value === '' || value === null || value === undefined) {
            setHitRateThresholds(prev => ({
                ...prev,
                [columnName]: 0
            }));
            return;
        }
        
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue)) {
            setHitRateThresholds(prev => ({
                ...prev,
                [columnName]: numericValue
            }));
        }
    };

    // Get numeric columns that can have thresholds
    const getNumericColumns = () => {
        if (!selectedPlayerData || selectedPlayerData.length === 0) return [];
        
        return playerDataColumns.filter(column => {
            // Check if the column contains numeric data
            const sampleValue = selectedPlayerData[0][column];
            return !isNaN(parseFloat(sampleValue)) && isFinite(parseFloat(sampleValue));
        });
    };

    

    // Filter handling (simple demo: Last 10/5/All)
    const filteredLog = () => {
        if(!selectedPlayerData) return [];
        console.log('Selected player data:', selectedPlayerData, 'selectedFilter:', selectedFilter);
        console.log('playerDataColumns:', playerDataColumns);
        if (selectedFilter === 'Last 5 Games') return selectedPlayerData.slice(0, 5);
        if (selectedFilter === 'Last 10 Games') return selectedPlayerData.slice(0, 10); // already 5 in mock
        return selectedPlayerData;
    }

    const fetchPlayers = async () => {
        const sportId = betCardData && betCardData.sportType || null;
        const homeTeamId = betCardData && betCardData.homeTeamStatspyderId || null;
        const awayTeamId = betCardData && betCardData.awayTeamStatspyderId || null;

        if (sportId && homeTeamId && awayTeamId) {
            const args = [
                {
                    teamName: betCardData.homeTeam,
                    teamId: homeTeamId,
                    sportId: sportId
                },
                {
                    teamName: betCardData.awayTeam,
                    teamId: awayTeamId,
                    sportId: sportId
                }
            ];

            Promise.all(
                args.map(arg =>
                    getPlayers({
                        'teamId': arg['teamId'],
                        'sport': arg['sportId']
                    })
                        .then(response => ({ teamName: arg.teamName, data: response.data }))
                        .catch(error => ({ teamName: arg.teamName, error }))
                )
            ).then(results => {
                let newPlayers = { ...selectablePlayers };
                results.forEach(result => {
                    if (result.data && result.data.success) {
                        newPlayers[result.teamName] = result.data.results && result.data.results.length > 0 ? result.data.results : {};
                    } else if (result.error) {
                        console.error(`Error fetching players for ${result.teamName}:`, result.error);
                    }
                });
                setSelectablePlayers(newPlayers);
                console.log('Players:', newPlayers);
            })
                .catch(error => {
                    console.error('Error fetching players:', error);
                });
        }
    }
    
    

    const displayPlayers = () => {
        console.log('Players:', selectablePlayers, 'selectedOpponent:', selectedTeam);
        const playersData = [];
        const availableTeams = Object.keys(selectablePlayers);
        if(availableTeams.length > 0 && availableTeams.indexOf(selectedTeam.teamName) > -1) {
            selectablePlayers[selectedTeam.teamName].forEach(player => {
                playersData.push({
                    playerName: player.player_name,
                    playerId: player.statspyder_player_id
                });
            });
        }
        
        if (playersData.length === 0) {
            return <option value="">No players available</option>;
        }

        let newSelectedPlayer = null;
        if(selectedPlayer === null) {
            newSelectedPlayer = playersData[0].playerId;
            setTimeout(() => {
                setSelectedPlayer(newSelectedPlayer);
            }, 100);
        }
        
        return playersData.map(player => {
            if (!!newSelectedPlayer) {
                return(<option key={player.playerId} value={player.playerId} selected={player.playerId === selectedPlayer}>{player.playerName}</option>);
            }
            else {
                return(<option key={player.playerId} value={player.playerId}>{player.playerName}</option>);
            }
        });
    }

    const displayTeams = () => {
        let teamOptions = [];
        if(selectedTeam === null) {
            selectableTeams().forEach((o, idx) => {
                if(idx == 0) {
                    teamOptions.push(<option key={idx+o.teamId} value={o.teamName} selected>{o.teamName}</option>);
                    setSelectedTeam(o);
                } else {
                    teamOptions.push(<option key={idx+o.teamId} value={o.teamName}>{o.teamName}</option>);
                }
            });
        }
        else{
            selectableTeams().forEach((o, idx) => {
                if(idx == 0) {
                    teamOptions.push(<option key={idx+o.teamId} value={o.teamName} selected>{o.teamName}</option>);
                    // setSelectedTeam(o);
                } else {
                    teamOptions.push(<option key={idx+o.teamId} value={o.teamName}>{o.teamName}</option>);
                }
            });
        }
        
        return teamOptions;
    }

    const updateSelectedTeam = (teamName) => {
        console.log('Selected team ID:', teamName);
        const teamObject = selectableTeams().find(team => team.teamName === teamName);
        if (teamObject) {
            console.log('Selected team object:', teamObject);
            setTimeout(() => {
                setSelectedTeam(teamObject);
                setSelectedPlayer(null);
            }, 100);
        }
    }

    const updateSelectedPlayer = (value) => {
        console.log('Selected player:', value);
        setSelectedPlayer(value);
    }


    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleRow}>
                    <span className={styles.title}>Player Prop Analyzer</span>
                </div>
                <div className={styles.selectors}>
                    <select className={styles.select} onChange={(e) => updateSelectedPlayer(e.target.value)}>
                        {displayPlayers()}
                    </select>
                    <select className={styles.select} onChange={(e) => updateSelectedTeam(e.target.value)}>
                        {displayTeams()}
                    </select>
                    <select className={styles.select} value={selectedFilter} onChange={(e) => setSelectedFilter(e.target.value)} disabled>
                        {/* <option value="Last 5">Last 5 Games</option> */}
                        <option value="Last 10">Last 10 Games</option>
                        {/* <option value="All Games">All Games</option> */}
                    </select>
                </div>
            </div>

            
                <div className={styles.quickInsights}>
                    <div className={styles.quickInsightsTitle}>
                        Quick Insights
                        <button 
                            className={`${styles.toggleButton} ${showQuickInsights ? styles.toggleButtonActive : ''}`}
                            onClick={() => setShowQuickInsights(!showQuickInsights)}
                        >
                            {showQuickInsights ? 'Hide' : 'Show'}
                        </button>
                    </div>

                    <div className={styles.quickRow}>
                        {playerPropInsights && showQuickInsights && (
                            <>
                                {/* Last Games Stats */}
                                {playerPropInsights.last_10 && (
                                    <div className={styles.insightSection}>
                                        <div className={styles.sectionTitle}>
                                            Last {playerPropInsights.last_10.records} Games
                                        </div>
                                        <div className={styles.statsGrid}>
                                            {Object.entries(playerPropInsights.last_10.averages).map(([key, value]) => (
                                                <div key={key} className={styles.quickItem}>
                                                    <strong>{getColumnName(key)}:</strong> {typeof value === 'number' ? value.toFixed(1) : value}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {/* Against Opponent Stats */}
                                {playerPropInsights.last_10_opp && (
                                    <div className={styles.insightSection}>
                                        <div className={styles.sectionTitle}>
                                            Last {playerPropInsights.last_10_opp.records} Games vs Opponent
                                        </div>
                                        <div className={styles.statsGrid}>
                                            {Object.entries(playerPropInsights.last_10_opp.averages).map(([key, value]) => (
                                                <div key={key} className={styles.quickItem}>
                                                    <strong>{getColumnName(key)}:</strong> {typeof value === 'number' ? value.toFixed(1) : value}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        
                        {!playerPropInsights && selectedPlayer && (
                            <div className={styles.noInsights}>
                                No insights available for selected player
                            </div>
                        )}
                    </div>
                </div>
            

            {/* Hit Rate Calculator Section */}
            {selectedPlayerData && getNumericColumns().length > 0 && (
                <div className={styles.hitRateCalculator}>
                    <div className={styles.hitRateTitle}>
                        <span>Hit Rate Calculator</span>
                        <button 
                            className={`${styles.toggleButton} ${showHitRateCalculator ? styles.toggleButtonActive : ''}`}
                            onClick={() => setShowHitRateCalculator(!showHitRateCalculator)}
                        >
                            {showHitRateCalculator ? 'Hide' : 'Show'}
                        </button>
                    </div>
                    {showHitRateCalculator && (
                        <>
                            <div className={styles.hitRateControls}>
                                <select
                                    className={styles.statSelect}
                                    value={selectedStat || ''}
                                    onChange={(e) => setSelectedStat(e.target.value)}
                                >
                                    {getNumericColumns().map((column) => (
                                        <option key={column} value={column}>
                                            {getColumnName(column)}
                                        </option>
                                    ))}
                                </select>

                                <input
                                    type="number"
                                    className={styles.thresholdInput}
                                    placeholder="Threshold"
                                    value={hitRateThresholds[selectedStat] || ''}
                                    onChange={(e) => updateThreshold(selectedStat, e.target.value)}
                                    step="0.1"
                                />
                            </div>
                            {selectedStat && (
                                <div className={styles.hitRateDisplay}>
                                    <div className={styles.hitRateCard}>
                                        <div className={styles.statHeader}>
                                            <span className={styles.statName}>{getColumnName(selectedStat)}</span>
                                            <span className={styles.thresholdValue}>
                                                Threshold: {hitRateThresholds[selectedStat] || 0}
                                            </span>
                                        </div>

                                        <div className={styles.hitRateStats}>
                                            <div className={styles.hitRateRow}>
                                                <span className={styles.hitRateLabel}>Hit Rate:</span>
                                                <span
                                                    className={styles.hitRateValue}
                                                    style={{
                                                        color: calculateHitRate(selectedStat, hitRateThresholds[selectedStat] || 0, filteredLog()).percentage >= 50 ? 'var(--bloomberg-green)' : 'var(--bloomberg-red)'
                                                    }}
                                                >
                                                    {calculateHitRate(selectedStat, hitRateThresholds[selectedStat] || 0, filteredLog()).hits}/{calculateHitRate(selectedStat, hitRateThresholds[selectedStat] || 0, filteredLog()).total} ({calculateHitRate(selectedStat, hitRateThresholds[selectedStat] || 0, filteredLog()).percentage}%)
                                                </span>
                                            </div>
                                            <div className={styles.hitRateRow}>
                                                <span className={styles.hitRateLabel}>Implied Odds:</span>
                                                <span className={styles.hitRateValue}>
                                                    {calculateHitRate(selectedStat, hitRateThresholds[selectedStat] || 0, filteredLog()).impliedProbability > 0 ? `+${calculateHitRate(selectedStat, hitRateThresholds[selectedStat] || 0, filteredLog()).impliedProbability}` : 'N/A'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            
                {/* 
                  Game Log Table Section
                  - Shows a table of player prop game logs if available.
                  - Table is only shown if showGameLog is true and there is data.
                  - Otherwise, a "no data" message is displayed.
                */}
                <div className={styles.tableWrapper}>
                    {filteredLog().length > 0 && showGameLog ? (
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    {playerDataColumns.map((c, idx) => (
                                        <th key={c + idx} className={styles.th}>
                                            {getColumnName(c)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLog().map((row, idx) => (
                                    <tr key={idx}>
                                        {playerDataColumns.map((c) => {
                                            const value = row[c];
                                            // Optionally color-code result columns if needed in the future
                                            // Example:
                                            // if (c === 'propResultPts' || c === 'propResultHR') {
                                            //     return (
                                            //         <td key={`${idx}-${c}`} className={styles.td}>
                                            //             <span className={styles.resultCell} style={{ color: getResultColor(value) }}>{value}</span>
                                            //         </td>
                                            //     );
                                            // }
                                            return (
                                                <td key={`${idx}-${c}`} className={styles.td}>
                                                    {String(value ?? '')}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className={styles.noData}>No player prop game log available.</div>
                    )}
                </div>
        </div>
    );
};

export default PlayerPropAnalyzer;


